import { useState } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useQuery } from '@tanstack/react-query';
import { PurchaseRequestSchema } from '@taqon/contracts';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { getPage } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { exportHref } from '../../shared/export/export-href';
import { SearchFilterBar } from '../../shared/ui/filters';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { UrlDimensionNote } from '../reporting/dimensions';
import { Pager } from '../crm/common';
import { RecordLink } from '../finance/detail';
import { MY_PR_API, PR_API, PR_STATUS_LABELS, PR_TYPE } from './purchasing';

const PAGE_SIZE = 50;

/**
 * Purchase requests (FRD-PRC-023, PRC-064): tracked by status, date and number, filtered and paged
 * on the server and kept in the URL. `mine` is the self-service view of the caller's own requests
 * (FRD-PRC-021), through the same view permission.
 */
export function PurchaseRequestsPage({ mine = false }: { mine?: boolean }) {
  const session = useSession();
  const canCreate = hasCapability(session.data, `${PR_TYPE}:create`);
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') ?? '');
  const filters = {
    status: params.get('status') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    search: params.get('search') ?? '',
  };
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const set = (key: keyof typeof filters | 'page', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: '-document_no',
  });
  for (const [key, value] of [
    ['status', filters.status],
    ['request_date_from', filters.from],
    ['request_date_to', filters.to],
    ['search', filters.search],
    ['department_id', params.get('department_id') ?? ''],
    // A dashboard's employee dimension is the requester (FRD-RPT-010).
    ['requester_employee_id', params.get('employee_id') ?? ''],
  ] as const) {
    if (value !== '') query.set(`filter[${key}]`, value);
  }
  const base = mine ? MY_PR_API : PR_API;
  const requests = useQuery({
    queryKey: ['procurement', 'purchase-requests', mine ? 'mine' : 'all', query.toString()],
    queryFn: () => getPage(`${base}?${query.toString()}`, PurchaseRequestSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title={mine ? 'My purchase requests' : 'Purchase requests'}
        description={
          mine
            ? 'The purchase requests raised for you, and where each one stands.'
            : 'Requisitions and their approval, conversion and status.'
        }
        actions={
          <>
            <ExportButton recordType={PR_TYPE} href={exportHref(base, query)} />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={RouterLink}
                to="/procurement/purchase-requests/new"
              >
                New purchase request
              </Button>
            )}
          </>
        }
      />
      <UrlDimensionNote keys={['department_id', 'employee_id']} />
      <SearchFilterBar
        aria-label="Purchase request filters"
        label="Search by number or justification"
        value={search}
        onChange={setSearch}
        onSubmit={() => {
          set('search', search.trim());
        }}
      >
        <TextField
          select
          size="small"
          label="Status"
          // "All" is the empty value; without these the select would show it blank.
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          value={filters.status}
          onChange={(e) => {
            set('status', e.target.value);
          }}
        >
          <MenuItem value="">All</MenuItem>
          {Object.entries(PR_STATUS_LABELS).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="date"
          label="Requested from"
          value={filters.from}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('from', e.target.value);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Requested to"
          value={filters.to}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('to', e.target.value);
          }}
        />
      </SearchFilterBar>
      <Section title={mine ? 'My requests' : 'Requests'}>
        {requests.isPending && <LoadingState label="Loading purchase requests" />}
        {requests.isError && <ErrorState message={requests.error.message} />}
        {requests.data?.items.length === 0 && (
          <EmptyState
            title="No purchase requests"
            detail={
              canCreate ? 'Raise one with “New purchase request”.' : 'None matches these filters.'
            }
          />
        )}
        {requests.data !== undefined && requests.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Purchase requests">
              <TableHead>
                <TableRow>
                  <TableCell>Number</TableCell>
                  <TableCell>Date</TableCell>
                  {!mine && <TableCell>Requester</TableCell>}
                  <TableCell>Justification</TableCell>
                  <TableCell>Preferred supplier</TableCell>
                  <TableCell align="right">Estimated total</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.data.items.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/procurement/purchase-requests/${r.id}`}>
                        {r.documentNo}
                      </RecordLink>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.requestDate}</TableCell>
                    {!mine && (
                      <TableCell>
                        {r.requesterLabel == null ? (
                          '—'
                        ) : (
                          <PersonName name={r.requesterLabel} employeeId={r.requesterEmployeeId} />
                        )}
                      </TableCell>
                    )}
                    <TableCell>{r.justification}</TableCell>
                    <TableCell>{r.preferredSupplierLabel ?? '—'}</TableCell>
                    <TableCell align="right">{r.estimatedTotal}</TableCell>
                    <TableCell>
                      <StatusChip status={r.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={requests.data.total}
              noun="requests"
              onPage={(next) => {
                set('page', String(next));
              }}
            />
          </>
        )}
      </Section>
    </Stack>
  );
}

export function MyPurchaseRequestsPage() {
  return <PurchaseRequestsPage mine />;
}
